import type {
  AnchorType,
  ElementAnchor,
} from '@src/editor/models/layout/types';
import type { Timeline } from '@src/editor/models/Timeline';
import { Element } from '../Element';

/**
 * AnchorResolver
 * Stateless utility for resolving scene/layout anchors to absolute timeline times.
 * Co-located with layout models for shared access by LayoutEngine and related services.
 */
export class AnchorResolver {
  /**
   * Resolves an anchor to an absolute time in seconds on the timeline.
   * @throws Error if referenced element is not found.
   */
  resolve(anchor: AnchorType, timeline: Timeline): number {
    if (anchor === 'clip-start') {
      return 0;
    }

    if (anchor === 'clip-end') {
      return timeline.duration;
    }

    // Element-based anchor
    const elementAnchor = anchor as ElementAnchor;
    const element = timeline.getElement(elementAnchor.elementId);

    if (!element) {
      throw new Error(
        `Element with ID ${elementAnchor.elementId} not found in timeline`,
      );
    }

    return this.resolveWithElement(elementAnchor, element);
  }

  resolveWithElement(elementAnchor: ElementAnchor, element: Element): number {
    if (elementAnchor.elementId !== element.id) {
      throw new Error(
        `Element ID mismatch: expected ${elementAnchor.elementId}, got ${element.id}`,
      );
    }

    const elementTime = element.time || 0;
    const elementDuration =
      typeof element.duration === 'number' ? element.duration : 0;
    const relativeTime = elementAnchor.relativeTime ?? 0;

    // If end is true, anchor to element end; otherwise anchor to element start + relativeTime
    if (elementAnchor.end) {
      return elementTime + elementDuration;
    } else {
      return elementTime + relativeTime;
    }
  }
}
